/**
 * Minimal editor surface needed to decide if the caret is in a section heading.
 * Node views pass the live Tiptap editor; tests pass a stub.
 */
export type SectionHeadingFocusEditor = {
  isFocused: boolean;
  state: {
    selection: {
      from: number;
      to?: number;
    };
  };
};

/**
 * Minimal node surface: the leading heading's type and `nodeSize`.
 * Real ProseMirror nodes satisfy this; missing `firstChild` is treated as unfocused.
 */
export type SectionHeadingFocusNode = {
  firstChild?: { type: { name: string }; nodeSize: number } | null;
};

/**
 * True when the editor's selection is entirely inside this section's leading
 * heading. Callers stamp `data-heading-focused` from this so the shared
 * stylesheet can show the empty-title placeholder only while the caret is in
 * the title.
 *
 * Does not consult `isEditable` — node views AND that when writing the attr
 * so read-only surfaces never show "New Section".
 */
export function isSectionHeadingFocused(
  editor: SectionHeadingFocusEditor,
  pos: number | undefined | null,
  node: SectionHeadingFocusNode
): boolean {
  if (!editor.isFocused) return false;
  if (typeof pos !== 'number') return false;

  const heading = node.firstChild;
  if (!heading || heading.type.name !== 'heading') return false;

  const headingStart = pos + 1;
  const headingEnd = headingStart + heading.nodeSize;
  const { from, to = from } = editor.state.selection;

  // Body starts at `headingEnd`. A NodeSelection of the heading has
  // `to === headingEnd`; a caret at the start of the body has `from === headingEnd`.
  return from >= headingStart && from < headingEnd && to <= headingEnd;
}
